import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { NotificationListSkeleton } from '../../components/Skeleton';
import { AppContext } from '../../context/ContextProvider';
import { AppEventTypes } from '../../types/AppType';
import { ObjectType } from '../../types/commonTypes';
import { NavigationPath } from '../../types/DomainTypes';
import { sortItems } from '../../utils';
import useFadeInOnScroll from '../hooks/useFadeInOnScroll';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import ProtectedPageLayout from '../shared/ProtectedPageLayout';
import { GET_NOTIFICATIONS } from './graphql/getNotifications';
import { UPDATE_NOTIFICATION_ITEM } from './graphql/updateNotificationItem';
import { NotificationTemplate } from './NotificationTemplate';
import { NotificationType } from './types';

const getBlankNotification = (options = {} as ObjectType) => {
  return {
    id: '',
    summary: '',
    createdDateTime: '',
    description: '',
    type: '',
    isRead: false,
    sortingId: 0,
    category: '',
    updatedDateTime: '',
    isToday: false,
    showClearButton: false,
    ...options,
  };
};

export const Notifications = () => {
  const { t } = useTranslation();
  const {
    dispatch,
    state: {
      app: { hasNotifications },
    },
  } = useContext(AppContext);

  const [fetchNotifications, { data: fetchNotificationsResult, loading: loadingNotifications }] =
    useLazyQuery(GET_NOTIFICATIONS);

  const [updateNotificationItem] = useMutation(UPDATE_NOTIFICATION_ITEM);
  const [currentNotifications, setCurrentNotifications] = useState<NotificationType[]>();
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const notifications = useMemo(() => {
    if (currentNotifications?.length === 0) {
      setIsProcessing(false);
      return [];
    }

    const items = (currentNotifications || []).map((x) => ({
      ...x,
      sortingId: moment(x.createdDateTime).toDate().getTime(),
      isToday: moment(x.createdDateTime).isSame(moment(), 'day'),
    }));

    const unread = items.filter((x) => !x.isRead);
    const hasUnread = unread.length > 0;
    if (!hasUnread && hasUnreadNotifications) {
      setHasUnreadNotifications(false);
    } else if (hasUnread && !hasUnreadNotifications) {
      setHasUnreadNotifications(true);
    }

    let todayNotifications = items.filter((x) => x.isToday);
    if (todayNotifications.length > 0) {
      todayNotifications = [
        {
          ...getBlankNotification({
            id: 'todayHeader',
            type: 'HEADER',
            summary: t('notifications:today'),
            showClearButton: hasUnread,
          }),
        },
        ...todayNotifications,
      ];
    }

    let prevNotifications = items.filter((x) => !x.isToday);
    if (prevNotifications.length > 0) {
      prevNotifications = [
        {
          ...getBlankNotification({
            id: 'prevHeader',
            type: 'HEADER',
            summary: t('notifications:previous'),
            showClearButton: todayNotifications.length === 0 && hasUnread,
          }),
        },
        ...prevNotifications,
      ];
    }
    setIsProcessing(false);
    return [...todayNotifications, ...prevNotifications];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNotifications]);

  const { isLoading, results: datasource, lastItemRef } = useInfiniteScroll(notifications);
  const { isItemVisible, fadeOnScrollClassName } = useFadeInOnScroll({ isLoading });

  const onUpdate = (itemId: string, markAsRead: boolean) => {
    let otherItems: NotificationType[];
    let unSortedItem: NotificationType[];
    let selectedItem: NotificationType | undefined;
    let idsToUpdate: string[] = [];
    if (itemId === '') {
      otherItems = (currentNotifications || []).filter((x) => x.isRead !== markAsRead);
      unSortedItem = (currentNotifications || []).filter((x) => x.isRead === markAsRead);
      unSortedItem = [...unSortedItem, ...(otherItems || []).map((x) => ({ ...x, isRead: markAsRead }))];
      idsToUpdate = unSortedItem.map((x) => x.id);
    } else {
      otherItems = (currentNotifications || []).filter((x) => x.id !== itemId);
      selectedItem = currentNotifications?.find((x) => x.id === itemId);
      unSortedItem = [...(otherItems || []), { ...selectedItem!, isRead: markAsRead }];
      idsToUpdate = [itemId];
    }
    updateNotificationItem({
      variables: {
        request: {
          ids: idsToUpdate,
          isRead: markAsRead,
        },
      },
    });

    setCurrentNotifications(sortItems(unSortedItem, false, 'sortingId'));
  };

  useEffect(() => {
    if (!loadingNotifications && fetchNotificationsResult) {
      const notificationResponse = fetchNotificationsResult.portalNotifications;
      if (notificationResponse && notificationResponse.results?.length > 0) {
        const items = (notificationResponse.results as NotificationType[]).map((x) => ({
          ...x,
          sortingId: moment(x.createdDateTime).toDate().getTime(),
        }));
        setCurrentNotifications(sortItems(items, false, 'sortingId'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNotificationsResult]);

  useEffect(() => {
    if (hasUnreadNotifications !== hasNotifications) {
      dispatch({
        type: AppEventTypes.UPDATE_STATE,
        payload: {
          hasNotifications: hasUnreadNotifications,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnreadNotifications]);

  useEffect(() => {
    setIsProcessing(true);
    fetchNotifications({
      variables: { from: 0, pageSize: 100000 },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProtectedPageLayout
      view={NavigationPath.NOTIFICATIONS}
      title={t('notifications:title')}
      onBack={() => null}
      showBackButton={false}
    >
      <div className="flex flex-col w-full h-full bg-gray-50 dark:bg-slate-900 overflow-y-auto">
        {(loadingNotifications || isProcessing) && (
          <div className="p-4">
            <NotificationListSkeleton count={5} />
          </div>
        )}
        {!loadingNotifications && !isProcessing && datasource?.length === 0 && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-gray-400 dark:text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                {t('notifications:noNotifications', 'No notifications')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('notifications:noNotificationsDesc', "You're all caught up!")}
              </p>
            </div>
          </div>
        )}
        {datasource?.length > 0 && (
          <div className="flex flex-col pb-5 px-4 sm:px-6">
            <NotificationTemplate
              preformanceConfig={{
                fadeOnScrollClassName,
                isItemVisible,
                lastItemRef,
              }}
              hasUnreadNotifications={hasUnreadNotifications}
              className=""
              notifications={datasource}
              onUpdate={(itemId: string) => onUpdate(itemId, true)}
              buttonText={t('notifications:mark_as_read_text', 'Mark as read')}
              buttonClassname="text-orange dark:text-teal-400 hover:text-orange/80 dark:hover:text-teal-300"
            />
          </div>
        )}
      </div>
    </ProtectedPageLayout>
  );
};
